import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Tooltip,
  Checkbox,
  TablePagination,
  TextField
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PrintIcon from '@mui/icons-material/Print';
import { getStatusDisplay, getStatusChipSx, getStatusChipVariant } from '../constants/equipmentStatus';

const ReceiveRecentItems = ({
  recentItems = [],
  onRefresh,
  onEdit,
  onViewHistory,
  onView,
  onPrintQR,
  onPrintBulkQR,
  loading = false,
  warehouses = [],
  racks = [],
  bays = []
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20); // Changed from 10 to 20
  const [selected, setSelected] = useState([]);
  const [searchText, setSearchText] = useState('');

  // Listen for equipment status changes from Quick Actions or other components
  useEffect(() => {
    const handleEquipmentChange = (event) => {
      console.log('🔄 ReceiveRecentItems received equipmentStatusChanged event:', event.detail);
      console.log('   Refreshing recent items list...');
      // Trigger refresh of recent items
      if (onRefresh) {
        onRefresh();
      }
    };

    window.addEventListener('equipmentStatusChanged', handleEquipmentChange);
    console.log('✅ ReceiveRecentItems: Listening for equipment status changes');

    return () => {
      window.removeEventListener('equipmentStatusChanged', handleEquipmentChange);
    };
  }, [onRefresh]);

  // Filter items by search text
  const filteredItems = recentItems.filter(item => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      item.equipment_id?.toLowerCase().includes(search) ||
      item.manufacturer?.toLowerCase().includes(search) ||
      item.model?.toLowerCase().includes(search) ||
      item.description?.toLowerCase().includes(search) ||
      item.job_name?.toLowerCase().includes(search)
    );
  });

  // Calculate displayed items based on pagination and search
  const displayItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = displayItems.map((item) => item.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBulkPrint = () => {
    const selectedItems = recentItems.filter(item => selected.includes(item.id));
    if (onPrintBulkQR) {
      onPrintBulkQR(selectedItems);
    }
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  return (
    <Paper sx={{ overflow: 'hidden', mt: 3, width: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Recently Received Equipment
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selected.length > 0 && (
              <Button
                startIcon={<PrintIcon />}
                onClick={handleBulkPrint}
                variant="outlined"
                size="small"
              >
                Print {selected.length} QR Codes
              </Button>
            )}
            <Tooltip title="Refresh list">
              <IconButton onClick={onRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <TextField
          placeholder="Search by ID, manufacturer, model, description, or job..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(0); // Reset to first page when searching
          }}
          size="small"
          fullWidth
          sx={{ maxWidth: 500 }}
        />
      </Box>

      {displayItems.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No recently received equipment found.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < displayItems.length}
                      checked={displayItems.length > 0 && selected.length === displayItems.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  <TableCell>Equipment ID</TableCell>
                  <TableCell>Barcode</TableCell>
                  <TableCell>Manufacturer</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Job</TableCell>
                  <TableCell>Warehouse</TableCell>
                  <TableCell>Rack</TableCell>
                  <TableCell>Bay</TableCell>
                  <TableCell>Received By</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayItems.map((item) => {
                  const isItemSelected = isSelected(item.id);
                  return (
                    <TableRow
                      key={item.id}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      selected={isItemSelected}
                      onClick={() => onViewHistory?.(item)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, item.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.equipment_id || item.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.barcode || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.manufacturer || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.model || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {item.description || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusDisplay(item.status).label}
                          sx={getStatusChipSx(item.status, getStatusChipVariant(item.status))}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {item.job_name ? (
                          <Box sx={{ maxWidth: 150 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.job_id || item.job_number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {item.job_name}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            // Check if item has job with stackLocations (new format)
                            if (item.job_id && item.stack_locations && item.stack_locations.length > 0) {
                              // For now, use first stack location
                              const location = item.stack_locations[0];
                              const warehouse = warehouses.find(w => w.id === location.warehouseId);
                              return warehouse?.name || 'Unknown';
                            }
                            // Fallback to API joined fields or item location
                            return item.job_stack_warehouse_name || item.warehouse_name || 'Unassigned';
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            // Check if item has job with stackLocations (new format)
                            if (item.job_id && item.stack_locations && item.stack_locations.length > 0) {
                              // For now, use first stack location
                              const location = item.stack_locations[0];
                              const rack = racks.find(r => r.id === location.rackId);
                              return rack?.name || 'Unknown';
                            }
                            // Fallback to API joined fields or item location
                            return item.job_stack_rack_name || item.rack_name || 'Unassigned';
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            // Check if item has job with stackLocations (new format)
                            if (item.job_id && item.stack_locations && item.stack_locations.length > 0) {
                              // For now, use first stack location
                              const location = item.stack_locations[0];
                              const bay = bays.find(b => b.id === location.bayId);
                              return bay?.name || 'Unknown';
                            }
                            // Fallback to API joined fields or item location
                            return item.job_stack_bay_name || item.bay_name || 'Unassigned';
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {item.received_by || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {formatDate(item.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit equipment">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.(item);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print QR Code">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onPrintQR?.(item);
                              }}
                            >
                              <QrCodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View history">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewHistory?.(item);
                              }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[20, 50, 100]}
            component="div"
            count={filteredItems.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Paper>
  );
};

export default ReceiveRecentItems;